import {useTranslation} from 'react-i18next';
import {Stack} from '@mui/material';
import {useEffect, useState} from 'react';
import {EntityTemplate} from '../../../../../../backend/responses/entity-template';
import FlatInput from '../../../../../../components/flat-input/flat-input';
import {translations} from '../../../../../../i18n/translations';
import {Modal, ModalProps} from '../../../../../../components/modal/modal';
import {
  MAX_TEMPLATE_DESCRIPTION_CHARACTERS,
  MAX_TEMPLATE_NAME_CHARACTERS,
} from '../../../../../../components/care-plan/templates-modal';

export type TemplateAction = 'save' | 'import';

interface TemplateModalProps extends Omit<ModalProps, 'actions'> {
  open: boolean;
  submitting?: boolean;
  template: EntityTemplate;
  onClose: () => void;
  onUpdate: (name: string, description: string) => void;
}

export const TemplatesEditNameModal = ({
  open,
  submitting,
  template,
  onClose,
  onUpdate,
}: TemplateModalProps) => {
  const {t} = useTranslation();
  const [name, setName] = useState<string>(template.name);
  const [description, setDescription] = useState<string>(template.description ?? '');
  const [nameError, setNameError] = useState<string | undefined>();
  const [descriptionError, setDescriptionError] = useState<string | undefined>();

  useEffect(() => {
    if (name.trim().length > 0 && name.trim().length > MAX_TEMPLATE_NAME_CHARACTERS) {
      setNameError(
        t(translations.activities_type_entry.templates.name_error, {
          max: MAX_TEMPLATE_NAME_CHARACTERS,
        })
      );
    } else {
      setNameError(undefined);
    }
  }, [name, t]);

  useEffect(() => {
    if (
      description.trim().length > 0 &&
      description.trim().length > MAX_TEMPLATE_DESCRIPTION_CHARACTERS
    ) {
      setDescriptionError(
        t(translations.activities_type_entry.templates.description_error, {
          max: MAX_TEMPLATE_DESCRIPTION_CHARACTERS,
        })
      );
    } else {
      setDescriptionError(undefined);
    }
  }, [description, t]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      className="templates-modal"
      preventCloseClick
      title={`${t(translations.office.professional_caregiver.settings.templates.edit)} "${template.name}"`}
      actions={[
        {
          label: t(translations.modal.cancel),
          type: 'destructive',
          onClick: onClose,
        },
        {
          label: t(translations.modal['save']),
          type: 'nondestructive',
          onClick: () => onUpdate(name, description),
          loading: submitting,
          disabled:
            !!nameError ||
            !!descriptionError ||
            !name ||
            !description ||
            (name === template.name && description === template.description),
        },
      ]}
    >
      <Stack className="container">
        <FlatInput
          required
          label={t(translations.activities_type_entry.templates.name)}
          counter={{current: name.length, max: MAX_TEMPLATE_NAME_CHARACTERS}}
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          errors={nameError}
        />

        <FlatInput
          required
          label={t(translations.activities_type_entry.templates.description)}
          counter={{current: description.length, max: MAX_TEMPLATE_DESCRIPTION_CHARACTERS}}
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
          errors={descriptionError}
          rows={3}
          multiline
        />
      </Stack>
    </Modal>
  );
};
